import { AfterViewInit, Component, ElementRef, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { debounceTime, mergeMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Validators } from '@angular/forms';

import { AuthorityEnum } from '../../../models/enums/authority.enum';

import { IGenericBaryFormGroup } from '../../../models/forms/GenericBaryFormGroup';
import { LoginControls } from '../../../models/forms/builder/LoginFormBuilder';
import { Transporter } from '../../../models/transporter.model';
import { PATHS } from '../../../models/bary-paths.model';

import { LoginStep } from '../../../models/enums/login-step.enum';
import { LoginModalType } from '../../../models/enums/login-modal.enum';
import { LoginExistResultType } from '../../../models/enums/login-exist-result-type.enum';

import { BaryFormFactoryService } from '../../../services/bary-form-factory.service';
import { AccountService } from '../../../services/account.service';
import { CurrentDataService } from '../../../services/current-data-service';
import { InitService } from '../../../services/init.service';
import { LoginService } from '../../../services/login.service';
import { NgIf } from '@angular/common';
import { TranslateDirective } from '../../../directives/translate.directive';
import { BaryButtonComponent, BaryInputCheckboxComponent, BaryInputPasswordComponent, BaryInputTextComponent, BaryLinkComponent } from '../../shared/components';
import { FeatureUtil } from '../../shared/util/feature-util';

@Component({
  selector: 'jhi-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss'],
  standalone: true,
  imports: [NgIf, TranslateDirective, BaryInputTextComponent, TranslateModule, BaryInputPasswordComponent, BaryButtonComponent, BaryInputCheckboxComponent, BaryLinkComponent],
})
export class LoginFormComponent implements OnInit, AfterViewInit {
  readonly #loginService = inject(LoginService);
  readonly #router = inject(Router);
  readonly #bff = inject(BaryFormFactoryService);
  readonly #accountService = inject(AccountService);
  readonly #translationService = inject(TranslateService);
  readonly #currentDataService = inject(CurrentDataService);
  readonly #spinnerService = inject(NgxSpinnerService);
  readonly #initService = inject(InitService);

  @ViewChild('firstFocus') firstFocus!: ElementRef;
  @Input() loginType: LoginModalType = LoginModalType.LOGIN;
  @Input() description?: string;
  @Output() cancelEvent = new EventEmitter();
  @Output() redirectEvent = new EventEmitter();
  @Output() allowToRegisterEvent = new EventEmitter<string>();

  public authenticationError = false;
  public registrationError = false;
  public activationError = false;
  public helpFromSupportError = false;
  public suggestToUseUser = false;
  public isLoginExist = false;
  public isLoginType = false;
  public activationEmailSent = false;
  public activationEmailNotSent = false;
  public loginForm!: IGenericBaryFormGroup<LoginControls>;
  public step = LoginStep.INIT;
  public transporter?: Transporter;
  public spinnerName = undefined;
  public originLoginType!: LoginModalType;

  public ngOnInit(): void {
    this.loginForm = this.#bff.makeLoginForm();
    this.#currentDataService.getCurrentTransporter().subscribe((currentTransporter: Transporter) => {
      this.transporter = currentTransporter;
      this.isLoginType = this.loginType === LoginModalType.LOGIN;
      this.originLoginType = this.loginType;

      if (!this.isLoginType) {
        // if registration we force the login input to be an email
        this.loginForm.controls.email.addValidators(Validators.email);
      }

      if (!this.description) {
        this.description = this.#translationService.instant('login.description');
      }

      this.loginForm.controls.email.valueChanges.pipe(debounceTime(500)).subscribe(() => {
        // reset the step when email address changes.
        this.resetFlags();
      });
    });
  }

  public ngAfterViewInit(): void {
    setTimeout((): void => {
      const firstInput: HTMLInputElement | null = document.querySelector('jhi-login-modal input');
      if (firstInput) {
        firstInput.focus();
      }
    });
  }

  private resetFlags(): void {
    this.isLoginExist = false;
    this.suggestToUseUser = false;
    this.authenticationError = false;
    this.registrationError = false;
    this.activationError = false;
    this.helpFromSupportError = false;
    this.activationEmailSent = false;
    this.activationEmailNotSent = false;
    this.step = LoginStep.INIT;
  }

  private processLogin(force = false): void {
    this.loginForm.getAngularForm().markAllAsTouched();
    if (this.loginForm.getAngularForm().valid) {
      this.#spinnerService.show(this.spinnerName).then();

      this.precheckLogin().subscribe((continueLogin: boolean) => {
        if (continueLogin || force) {
          this.login();
        } else {
          this.#spinnerService.hide(this.spinnerName);
        }
      });
    }
  }

  private precheckLogin(): Observable<boolean> {
    return this.#accountService.checkIsEmailExist(this.loginForm.controls.email.value).pipe(mergeMap(isCheck => this.performEmailExistResult(isCheck)));
  }

  private login(): void {
    this.#loginService
      .loginAndFetchCustomer({
        username: this.loginForm.controls.email.value,
        password: this.loginForm.controls.password.value,
        rememberMe: this.loginForm.controls.rememberMe.value ?? true,
      })
      .subscribe(
        () => {
          this.#initService.fetchAll().subscribe({
            next: () => {
              this.authenticationError = false;
              let urlRedirect = null;
              /** On redirige vers la page d'accueil en fonction du role sauf si c'est un user dans le tunnel d'estimation **/

              const isAnalytics = FeatureUtil.isAnalyticsTransporter(this.transporter?.technicalName!);

              switch (this.#accountService.getAuthority()) {
                case AuthorityEnum.ADMIN:
                  urlRedirect = PATHS.ADMIN_USER_MANAGEMENT.path;
                  break;
                case AuthorityEnum.MODERATOR:
                  urlRedirect = isAnalytics ? PATHS.MIDDLE_TRANSPORTER_DASHBOARD.path : PATHS.MIDDLE_TRANSPORTER_STATISTICS.path;
                  break;
                case AuthorityEnum.USER:
                  if (!this.#router.url.startsWith(PATHS.ESTIMATE.path)) {
                    urlRedirect = PATHS.USER_MY_DASHBOARD.path;
                  }
                  break;
                default:
                  break;
              }
              if (urlRedirect) {
                this.#router.navigateByUrl(urlRedirect).then(() => {
                  this.#spinnerService.hide(this.spinnerName);
                  this.redirectEvent.emit();
                });
              } else {
                this.cancelEvent.emit();
                this.#spinnerService.hide();
              }
            },
          });
        },
        () => {
          this.authenticationError = true;
          this.#spinnerService.hide();
        }
      );
  }

  private performEmailExistResult(exist: LoginExistResultType): Observable<boolean> {
    this.isLoginExist = exist !== LoginExistResultType.NOT_EXIST;

    switch (exist) {
      case LoginExistResultType.ACTIVATED_AND_EXIST_WITH_CUSTOMER:
        this.registrationError = !this.isLoginType;
        this.step = this.isLoginType ? LoginStep.LOGIN : LoginStep.INIT;
        break;
      case LoginExistResultType.ACTIVATED_BUT_EXIST_WITHOUT_CUSTOMER:
        this.suggestToUseUser = true;
        this.step = LoginStep.SUGGEST_TO_USE_USER;
        break;
      case LoginExistResultType.NOT_ACTIVATED_BUT_EXIST_WITH_CUSTOMER:
        this.activationError = true;
        break;
      case LoginExistResultType.NOT_ACTIVATED_AND_EXIST_WITHOUT_CUSTOMER:
        this.helpFromSupportError = !this.isLoginType;
        this.authenticationError = this.isLoginType;
        break;
      default:
        this.allowToRegisterEvent.emit(this.loginForm.controls.email.value);
        this.authenticationError = true;
        break;
    }

    return of(this.step === LoginStep.LOGIN);
  }

  private register(): void {
    this.#router.navigateByUrl(PATHS.ACCOUNT_REGISTER.path).then(() => {
      this.loginType = LoginModalType.REGISTER;
      this.isLoginType = false;
      this.redirectEvent.emit();
    });
  }

  private requestResetPassword(): void {
    this.#router.navigateByUrl(PATHS.ACCOUNT_RESET_REQUEST.path).then(() => this.redirectEvent.emit());
  }

  private cancel(): void {
    this.authenticationError = false;
    this.loginForm.getAngularForm().patchValue({
      username: '',
      password: '',
      rememberMe: true,
    });
    this.cancelEvent.emit('cancel');
  }

  public showNextBt(): boolean {
    return this.step === LoginStep.INIT;
  }

  public showLoginBt(): boolean {
    return !this.suggestToUseUser && this.step === LoginStep.LOGIN;
  }

  public showForceLoginBt(): boolean {
    return this.step === LoginStep.LOGIN_AFTER_SUGGEST_TO_USE_USER;
  }

  public showSuggestToUseUserBt(): boolean {
    return this.suggestToUseUser && this.step === LoginStep.SUGGEST_TO_USE_USER;
  }

  public handleClick(action: any): void {
    if (action.login) {
      this.processLogin();
    } else if (action.next) {
      this.precheckLogin().subscribe();
    } else if (action.suggestToUseUser) {
      this.step = LoginStep.LOGIN_AFTER_SUGGEST_TO_USE_USER;
    } else if (action.forceLogin) {
      this.processLogin(true);
    } else if (action.createAccount) {
      this.register();
    } else if (action.reset) {
      this.requestResetPassword();
    } else if (action.switchToLogin) {
      this.loginType = LoginModalType.LOGIN;
      this.isLoginType = true;
      this.resetFlags();
    } else if (action.close) {
      this.cancel();
    } else {
      this.cancelEvent.emit('cancel');
    }
  }

  public enterAction(e: any): void {
    e.preventDefault();
    let action;
    if (this.showNextBt()) {
      action = { next: true };
    } else if (this.showLoginBt()) {
      action = { login: true };
    } else if (this.showSuggestToUseUserBt()) {
      action = { suggestToUseUser: true };
    } else if (this.showForceLoginBt()) {
      action = { forceLogin: true };
    }
    return this.handleClick(action);
  }

  public resendAccountActivationEmail(): void {
    this.#accountService.resendAccountActivationEmail(this.loginForm.controls.email.value).subscribe(
      () => {
        this.resetFlags();
        this.activationEmailSent = true;
      },
      () => {
        this.activationEmailNotSent = true;
      }
    );
  }
}
